import { apiClient } from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import { uploadAdminFiles } from "@/lib/files.api";
import type {
  AddVirtualClassroomParticipantsDto,
  BindVirtualClassroomFilesDto,
  UpdateVirtualClassroomDto,
  VirtualClassroomFileResDto,
  VirtualClassroomResDto,
} from "../types/virtual-classroom.types";

export type {
  AddVirtualClassroomParticipantsDto,
  BindVirtualClassroomFilesDto,
  CreateVirtualClassroomDto,
  UpdateVirtualClassroomDto,
  VirtualClassroomFileResDto,
  VirtualClassroomResDto,
  VirtualClassroomScheduledTimeDto,
} from "../types/virtual-classroom.types";

const VIRTUAL_CLASSROOMS_ENDPOINT = "/api/virtual-classrooms";
const CLIENT_VIRTUAL_CLASSROOMS_ENDPOINT = "/api/client/virtual-classrooms";
const EDUCATIONS_ENDPOINT = "/api/educations";

const REMOVED_USER_IDS_MAX = 20;
const ADD_PARTICIPANTS_USER_IDS_MAX = 20;
const BIND_FILES_MAX = 20;

/**
 * Admin — Sanal sınıf güncelleme
 * PATCH /api/virtual-classrooms/:id
 * Yetki: EDUCATIONS.UPDATE (JWT)
 *
 * Boş gövde → backend 400 VIRTUAL_CLASSROOM_NOTHING_TO_UPDATE
 * Geçersiz removedUserIds → 400 VIRTUAL_CLASSROOM_REMOVED_USER_NOT_PARTICIPANT
 */
export const virtualClassroomsApi = {
  /**
   * GET /api/educations/:educationId/virtual-classrooms
   * Swagger: VirtualClassroomResDtoPaginatedResponse — data: VirtualClassroomResDto[]
   */
  listByEducation: async (
    educationId: string,
    params?: { offeringId?: string },
  ): Promise<VirtualClassroomResDto[]> => {
    const { data: response } = await apiClient.get<
      | VirtualClassroomResDto[]
      | { data?: VirtualClassroomResDto[] }
    >(`${EDUCATIONS_ENDPOINT}/${educationId}/virtual-classrooms`, {
      params,
    });
    if (Array.isArray(response)) return response;
    const wrapped = response as { data?: VirtualClassroomResDto[] };
    return wrapped?.data ?? [];
  },

  /**
   * POST /api/virtual-classrooms/:id/participants
   * Body: { userIds } — 1..20 UUID (Swagger AddVirtualClassroomParticipantsDto)
   */
  addParticipants: async (
    id: string,
    userIds: string[],
  ): Promise<void> => {
    if (userIds.length < 1 || userIds.length > ADD_PARTICIPANTS_USER_IDS_MAX) {
      throw new Error(
        `Katılımcı eklerken userIds 1–${ADD_PARTICIPANTS_USER_IDS_MAX} arasında olmalıdır.`,
      );
    }
    const body: AddVirtualClassroomParticipantsDto = { userIds };
    await apiClient.post(
      `${VIRTUAL_CLASSROOMS_ENDPOINT}/${id}/participants`,
      body,
    );
  },

  /**
   * DELETE /api/virtual-classrooms/:id — sınıfı soft delete eder,
   * katılımcıların enrollment'ı ENROLLED ise APPROVED_BRANCH_MANAGER'a geri alınır.
   */
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`${VIRTUAL_CLASSROOMS_ENDPOINT}/${id}`);
  },

  /**
   * POST /api/virtual-classrooms/:id/complete — sınıfı tamamla,
   * katılımcılara push bildirim gönderir. Opsiyonel `body.decisions` verilirse
   * yalnız bu sınıfın katılımcılarının enrollment'ları state machine üzerinden
   * ilerletilir; eğitim+offering durumu DEĞİŞMEZ.
   */
  complete: async (
    id: string,
    body?: {
      decisions?: Array<{
        enrollmentId: string;
        decision: "COMPLETED" | "NOT_COMPLETED" | "BLACKLISTED";
        excuseReason?: number;
      }>;
      blacklist?: {
        reason: number;
        startDate: string;
        endDate: string;
        description?: string;
      };
    },
  ): Promise<VirtualClassroomResDto> => {
    const { data } = await apiClient.post<
      VirtualClassroomResDto | { data?: VirtualClassroomResDto }
    >(`${VIRTUAL_CLASSROOMS_ENDPOINT}/${id}/complete`, body ?? {});
    const entity =
      (data as { data?: VirtualClassroomResDto })?.data ??
      (data as VirtualClassroomResDto);
    if (!entity?.id) {
      throw new Error(i18n.t("virtualClassroomsApi.completeInvalid"));
    }
    return entity;
  },

  update: async (
    id: string,
    body: UpdateVirtualClassroomDto,
  ): Promise<VirtualClassroomResDto> => {
    if (
      body.removedUserIds &&
      body.removedUserIds.length > REMOVED_USER_IDS_MAX
    ) {
      throw new Error(
        `removedUserIds en fazla ${REMOVED_USER_IDS_MAX} olabilir.`,
      );
    }

    const { data } = await apiClient.patch<
      VirtualClassroomResDto | { data?: VirtualClassroomResDto }
    >(`${VIRTUAL_CLASSROOMS_ENDPOINT}/${id}`, body);

    const entity =
      (data as { data?: VirtualClassroomResDto })?.data ??
      (data as VirtualClassroomResDto);
    if (!entity?.id) {
      throw new Error(i18n.t("virtualClassroomsApi.updateInvalid"));
    }
    return entity;
  },

  /**
   * GET /api/virtual-classrooms/:id/files
   * Admin/officer/manager — sınıfa atanmış dosyaları listeler.
   */
  listFiles: async (id: string): Promise<VirtualClassroomFileResDto[]> => {
    const { data } = await apiClient.get<{
      items?: VirtualClassroomFileResDto[];
      data?: VirtualClassroomFileResDto[];
    }>(`${VIRTUAL_CLASSROOMS_ENDPOINT}/${id}/files`);
    const items = data?.items ?? data?.data;
    return Array.isArray(items) ? items : [];
  },

  /**
   * GET /api/client/virtual-classrooms/:id/files
   * Kursiyer — sınıfa katılımcıysa dosyaları listeler.
   */
  listFilesForClient: async (
    id: string,
  ): Promise<VirtualClassroomFileResDto[]> => {
    const { data } = await apiClient.get<{
      items?: VirtualClassroomFileResDto[];
      data?: VirtualClassroomFileResDto[];
    }>(`${CLIENT_VIRTUAL_CLASSROOMS_ENDPOINT}/${id}/files`);
    const items = data?.items ?? data?.data;
    return Array.isArray(items) ? items : [];
  },

  /**
   * GET /api/client/educations/:educationId/virtual-classroom-files
   * Bir eğitimin tüm sanal sınıflarına bağlı dosyaları (kursiyer için) tek
   * çağrıda döner. Her sınıf için ayrı listFilesForClient çağrısının yerini
   * alır.
   */
  listFilesForClientByEducation: async (
    educationId: string,
  ): Promise<VirtualClassroomFileResDto[]> => {
    const { data } = await apiClient.get<{
      items?: VirtualClassroomFileResDto[];
      data?: VirtualClassroomFileResDto[];
    }>(`/api/client/educations/${educationId}/virtual-classroom-files`);
    const items = data?.items ?? data?.data;
    return Array.isArray(items) ? items : [];
  },

  /**
   * Önce /api/admin/files/upload ile yükleyip sonra
   * POST /api/virtual-classrooms/:id/files ile sınıfa bind eder.
   * Tek atımlı helper — modal'larda upload-and-bind tek aksiyon.
   */
  uploadAndBindFiles: async (
    classroomId: string,
    files: File[],
    displayName?: string,
  ): Promise<VirtualClassroomFileResDto[]> => {
    if (files.length === 0) return [];
    if (files.length > BIND_FILES_MAX) {
      throw new Error(
        `Bir seferde en fazla ${BIND_FILES_MAX} dosya yüklenebilir.`,
      );
    }
    const uploaded = await uploadAdminFiles(files);
    const fileIds = uploaded.map((f) => f.id);
    if (fileIds.length === 0) return [];
    const body: BindVirtualClassroomFilesDto = {
      fileIds,
      ...(displayName ? { displayName } : {}),
    };
    const { data } = await apiClient.post<{
      items?: VirtualClassroomFileResDto[];
      data?: VirtualClassroomFileResDto[];
    }>(`${VIRTUAL_CLASSROOMS_ENDPOINT}/${classroomId}/files`, body);
    const items = data?.items ?? data?.data;
    return Array.isArray(items) ? items : [];
  },

  /**
   * DELETE /api/virtual-classrooms/:id/files/:fileId
   * Sadece join row'unu kaldırır; files tablosundaki kaynak dosya korunur.
   */
  unbindFile: async (classroomId: string, fileId: string): Promise<void> => {
    await apiClient.delete(
      `${VIRTUAL_CLASSROOMS_ENDPOINT}/${classroomId}/files/${fileId}`,
    );
  },
};
